import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getBackendUrl } from '../utils/api';

const API = getBackendUrl();

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [bookingInfo, setBookingInfo] = useState(null);
  const [error, setError] = useState('');
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setError('No session ID found');
      setStatus('error');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await fetch(`${API}/booking/status/${sessionId}`);
      const data = await response.json();
      
      if (data.success) {
        if (data.payment_status === 'paid' && data.booking_status === 'confirmed') {
          setBookingInfo({
            ticketNumber: data.ticket_number,
            bookingId: data.booking_id,
            message: data.message
          });
          setStatus('success');
        } else if (data.payment_status === 'paid') {
          // Payment received but still processing
          setStatus('processing');
          if (pollCount < 5) {
            setTimeout(() => {
              setPollCount(prev => prev + 1);
              checkPaymentStatus();
            }, 2000);
          } else {
            setBookingInfo({
              bookingId: data.booking_id,
              message: 'Payment received. Your ticket is being generated and will be emailed shortly.'
            });
            setStatus('success');
          }
        } else {
          // Still pending
          if (pollCount < 10) {
            setTimeout(() => {
              setPollCount(prev => prev + 1);
              checkPaymentStatus();
            }, 2000);
          } else {
            setError('Payment verification timeout. Please check your email or contact support.');
            setStatus('error');
          }
        }
      } else {
        setError(data.message || 'Failed to verify payment');
        setStatus('error');
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (pollCount < 3) {
        setTimeout(() => {
          setPollCount(prev => prev + 1);
          checkPaymentStatus();
        }, 2000);
      } else {
        setError('Unable to verify payment. Please check your email for confirmation.');
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0d1208] flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        
        {/* Checking Status */}
        {(status === 'checking' || status === 'processing') && (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-6 border-4 border-[#2a3325] border-t-[#5a6550] rounded-full animate-spin"></div>
            <h1 className="text-2xl font-bold text-[#e8e2d0] mb-4 tracking-wide">
              {status === 'checking' ? 'Verifying Payment...' : 'Processing Your Booking...'}
            </h1>
            <p className="text-[#9aa090]">
              Please wait while we confirm your payment
            </p>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="bg-[#1a2015]/80 rounded-lg p-8 border border-[#2a3325] text-center">
            {/* Success Icon */}
            <div className="w-20 h-20 mx-auto mb-6 bg-[#2a3325] rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-[#c9a962]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h1 className="text-3xl font-bold text-[#e8e2d0] mb-4 tracking-wide">
              BOOKING CONFIRMED
            </h1>
            
            {bookingInfo?.ticketNumber && (
              <div className="bg-[#0d1208] rounded-lg p-6 mb-6">
                <p className="text-[#6a7560] text-sm mb-1">Your Ticket Number</p>
                <p className="text-2xl font-mono font-bold text-[#c9a962] mb-4">
                  {bookingInfo.ticketNumber}
                </p>
                <a
                  href={`${API}/booking/ticket/${bookingInfo.ticketNumber}/download`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-ticket-btn"
                  className="inline-flex items-center px-4 py-2 bg-[#2a3325] hover:bg-[#3a4335] text-[#c9a962] rounded transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF Ticket
                </a>
              </div>
            )}
            
            <p className="text-[#9aa090] mb-8">
              {bookingInfo?.message || 'Your ticket has been sent to your email address.'}
            </p>
            
            <div className="space-y-4">
              <div className="bg-[#0d1208] rounded-lg p-4 text-left">
                <h3 className="text-[#e8e2d0] font-semibold mb-2">What's Next?</h3>
                <ul className="text-[#9aa090] text-sm space-y-2">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-2 text-[#8a8570] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Check your email for your ticket (including spam folder)
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-2 text-[#8a8570] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Save or print your PDF ticket
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-2 text-[#8a8570] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Arrive with plenty of time to set up and prepare
                  </li>
                </ul>
              </div>
              
              {/* Social Sharing */}
              <div className="bg-[#0d1208] rounded-lg p-4">
                <h3 className="text-[#e8e2d0] font-semibold mb-3 text-center">Share that you're attending!</h3>
                <div className="flex justify-center gap-3">
                  {/* Facebook Share */}
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}&quote=${encodeURIComponent(`I just booked my spot at ${bookingInfo?.eventName || 'a Fallen Dragon event'}! Join me for an immersive airsoft experience.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-[#1877f2] hover:bg-[#166fe5] text-white rounded-lg transition-colors"
                    data-testid="share-facebook-btn"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    Facebook
                  </a>
                  
                  {/* Twitter/X Share */}
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`I just booked my spot at ${bookingInfo?.eventName || 'a Fallen Dragon event'}! 🐉 Join me for an immersive airsoft experience.`)}&url=${encodeURIComponent(window.location.origin)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-[#000000] hover:bg-[#1a1a1a] text-white rounded-lg transition-colors"
                    data-testid="share-twitter-btn"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X
                  </a>
                  
                  {/* WhatsApp Share */}
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`I just booked my spot at ${bookingInfo?.eventName || 'a Fallen Dragon event'}! Join me for an immersive airsoft experience at ${window.location.origin}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-lg transition-colors"
                    data-testid="share-whatsapp-btn"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                </div>
              </div>
              
              <Link 
                to="/"
                className="block w-full py-3 bg-transparent hover:bg-[#8a8570]/20 text-[#d4cfc0] font-semibold tracking-[0.1em] border-2 border-[#8a8570] transition-all duration-300 text-center"
              >
                BACK TO HOME
              </Link>
            </div>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="bg-[#1a2015]/80 rounded-lg p-8 border border-[#2a3325] text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-[#3a2020] rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-[#e8a0a0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <h1 className="text-2xl font-bold text-[#e8e2d0] mb-4 tracking-wide">
              Something Went Wrong
            </h1>
            
            <p className="text-[#9aa090] mb-6">
              {error}
            </p>
            
            <p className="text-[#8a9580] text-sm mb-8">
              If you believe your payment was successful, please check your email for a confirmation 
              or contact us at <a href="mailto:info@fallendragonevents.co.uk" className="text-[#c9a962] underline">info@fallendragonevents.co.uk</a>
            </p>
            
            <div className="space-y-3">
              <button 
                onClick={() => {
                  setStatus('checking');
                  setPollCount(0);
                  checkPaymentStatus();
                }}
                className="block w-full py-3 bg-transparent hover:bg-[#8a8570]/20 text-[#d4cfc0] font-semibold tracking-[0.1em] border-2 border-[#8a8570] transition-all duration-300"
              >
                TRY AGAIN
              </button>
              
              <Link 
                to="/"
                className="block w-full py-3 bg-transparent text-[#8a9580] hover:text-[#e8e2d0] font-semibold tracking-wider border border-[#2a3325] transition-all duration-300 text-center"
              >
                BACK TO HOME
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingSuccess;
